/* AdminMgmtComms — Communications Hub.
 *
 * 4 tabs at /admin/comms:
 *   1. Email log (Resend deliverability)
 *   2. SMS log (Twilio deliverability)
 *   3. Templates (editable transactional templates)
 *   4. Announcements (in-app broadcasts)
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Mail, MessageSquare, FileEdit, Megaphone, Loader2, Send, Plus, Trash2,
} from "lucide-react";
import {
  MgmtKpi, MgmtSectionHeader, MgmtEmpty, ReasonField, TABS_STYLE, TabBtn,
} from "../../components/AdminMgmtCommon";

const TABS = [
  { id: "email",         label: "Email log",      icon: Mail },
  { id: "sms",           label: "SMS log",        icon: MessageSquare },
  { id: "templates",     label: "Templates",      icon: FileEdit },
  { id: "announcements", label: "Announcements",  icon: Megaphone },
];

export default function AdminMgmtComms() {
  const [tab, setTab] = useState("email");
  return (
    <div className="p-6 space-y-5 max-w-7xl" data-testid="admin-mgmt-comms">
      <PageHeader title="Communications" subtitle="Outbound deliverability · template editor · in-app announcements — admin oversight only."/>
      <div className={TABS_STYLE} data-testid="comms-tabs">
        {TABS.map(t => <TabBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)} icon={t.icon} label={t.label} testid={`comms-tab-${t.id}`}/>)}
      </div>
      {tab === "email" && <DeliveryLog kind="email"/>}
      {tab === "sms" && <DeliveryLog kind="sms"/>}
      {tab === "templates" && <TemplatesTab/>}
      {tab === "announcements" && <AnnouncementsTab/>}
    </div>
  );
}

function DeliveryLog({ kind }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(7);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const path = kind === "email" ? "email-log" : "sms-log";
      const r = await api.get(`/admin/mgmt/comms/${path}?days=${days}&q=${encodeURIComponent(q)}&status=${status}`);
      setData(r.data);
    } catch (e) { toast.error(`Failed to load ${kind} log`); }
    finally { setBusy(false); }
  }, [kind, days, q, status]);
  useEffect(() => { load(); }, [load]);

  const resend = async (id) => {
    const reason = window.prompt("Resend reason (min 3 chars · audit-logged)");
    if (!reason || reason.trim().length < 3) return;
    try { await api.post(`/admin/mgmt/comms/email-log/${id}/resend`, { reason: reason.trim() }); toast.success("Resent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>;
  const by = data.by_status || {};
  return (
    <div className="space-y-4">
      <MgmtSectionHeader title={`${kind === "email" ? "Email" : "SMS"} delivery log`} subtitle={`${data.total} sent · last ${days} days`} onRefresh={load} busy={busy}/>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
        <MgmtKpi label="Sent" value={by.sent || 0} tone="emerald"/>
        <MgmtKpi label="Delivered" value={by.delivered || 0} tone="emerald"/>
        <MgmtKpi label="Bounced" value={by.bounced || 0} tone={by.bounced > 0 ? "rose" : "neutral"}/>
        <MgmtKpi label="Failed" value={by.failed || 0} tone={by.failed > 0 ? "rose" : "neutral"}/>
        <MgmtKpi label="Opened" value={by.opened || 0}/>
      </div>
      <div className="flex gap-2 items-end">
        <div className="flex-1"><Label>Search</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={kind === "email" ? "to · subject" : "to · phone"} data-testid={`${kind}-search`}/></div>
        <div className="min-w-[140px]">
          <Label>Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid={`${kind}-status`}>
            <option value="">Any status</option>
            {Object.keys(by).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="min-w-[110px]">
          <Label>Window</Label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-full h-9 border rounded-md px-2 text-sm bg-background">
            <option value={1}>24h</option><option value={7}>7d</option><option value={30}>30d</option>
          </select>
        </div>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid={`${kind}-log-table`}>
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">To</th>
              {kind === "email" && <th className="px-3 py-2">Subject</th>}
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.id} data-testid={`${kind}-row-${r.id}`}>
                <td className="px-3 py-2 mono text-[11px]">{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                <td className="px-3 py-2 mono text-[11px]">{r.to}</td>
                {kind === "email" && <td className="px-3 py-2 max-w-md truncate">{r.subject}</td>}
                <td className="px-3 py-2">
                  <span className={`chip text-[10px] ${
                    r.status === "delivered" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
                    r.status === "failed" || r.status === "bounced" ? "bg-rose-50 border-rose-200 text-rose-700" :
                    "bg-foreground/5 border-foreground/10"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {kind === "email" && (
                    <Button size="sm" variant="ghost" onClick={() => resend(r.id)} data-testid={`${kind}-resend-${r.id}`}><Send className="w-3 h-3 mr-1"/>Resend</Button>
                  )}
                </td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && (
              <tr><td colSpan={5}><MgmtEmpty message={`No ${kind} sent in window. Outbound traffic is logged here once Resend/Twilio webhooks fire.`}/></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplatesTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/comms/templates"); setData(r.data); }
    catch (e) { toast.error("Failed to load templates"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="Email templates" subtitle={`${data.count || 0} templates · edits version-bumped & audit-logged`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="template-new-btn"><Plus className="w-3 h-3 mr-1"/>New</Button>}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="templates-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Key</th><th className="px-3 py-2">Subject</th><th className="px-3 py-2">Version</th><th className="px-3 py-2">Updated</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(t => (
              <tr key={t.key} data-testid={`template-row-${t.key}`}>
                <td className="px-3 py-2 mono text-[11px]">{t.key}</td>
                <td className="px-3 py-2 max-w-md truncate">{t.subject}</td>
                <td className="px-3 py-2 text-foreground/55 mono">v{t.version}</td>
                <td className="px-3 py-2 mono text-[11px]">{t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`template-edit-${t.key}`}>Edit</Button>
                </td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={5}><MgmtEmpty message="No templates yet. Create one to start editing email content from the admin without redeploying."/></td></tr>}
          </tbody>
        </table>
      </div>
      <TemplateDialog open={createOpen || !!editing} initial={editing} onClose={() => { setEditing(null); setCreateOpen(false); }} onSaved={load}/>
    </div>
  );
}

function TemplateDialog({ open, initial, onClose, onSaved }) {
  const [key, setKey] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKey(initial?.key || ""); setSubject(initial?.subject || ""); setBody(initial?.body_html || ""); setReason("");
  }, [open, initial]);

  const submit = async () => {
    if (reason.trim().length < 3 || !subject || !body || !key) { toast.error("All fields required"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/mgmt/comms/templates/${key}`, { key, subject, body_html: body, reason: reason.trim() });
      toast.success("Template saved"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{initial ? `Edit · ${initial.key}` : "New template"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Key</Label><Input value={key} onChange={(e) => setKey(e.target.value)} disabled={!!initial} data-testid="template-key"/></div>
          <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="template-subject"/></div>
          <div><Label>Body HTML</Label><Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="mono text-[12px]" data-testid="template-body"/></div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="template-save">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnnouncementsTab() {
  const [data, setData] = useState({ rows: [] });
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/admin/mgmt/comms/announcements"); setData(r.data); }
    catch (e) { toast.error("Failed to load announcements"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (aid) => {
    const reason = window.prompt("Disable reason (min 3 chars)");
    if (!reason || reason.trim().length < 3) return;
    try { await api.delete(`/admin/mgmt/comms/announcements/${aid}`, { data: { reason: reason.trim() } }); toast.success("Disabled"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <MgmtSectionHeader title="In-app announcements" subtitle={`${data.count || 0} broadcasts · push platform-wide or scope to one role`} onRefresh={load} busy={busy}
        action={<Button size="sm" onClick={() => setCreateOpen(true)} data-testid="ann-new-btn"><Plus className="w-3 h-3 mr-1"/>New</Button>}/>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="ann-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr><th className="px-3 py-2">Title</th><th className="px-3 py-2">Audience</th><th className="px-3 py-2">Severity</th><th className="px-3 py-2">Window</th><th className="px-3 py-2">State</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody className="divide-y">
            {(data.rows || []).map(r => (
              <tr key={r.id} data-testid={`ann-row-${r.id}`}>
                <td className="px-3 py-2 font-medium max-w-xs truncate">{r.title}</td>
                <td className="px-3 py-2"><span className="chip text-[10px] bg-foreground/5">{r.audience}</span></td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10px] ${r.severity === "critical" ? "bg-rose-50 border-rose-200 text-rose-700" : r.severity === "warn" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-sky-50 border-sky-200 text-sky-700"}`}>
                    {r.severity}
                  </span>
                </td>
                <td className="px-3 py-2 mono text-[10.5px]">{(r.starts_at || "").slice(0, 10) || "now"} → {(r.ends_at || "").slice(0, 10) || "∞"}</td>
                <td className="px-3 py-2"><span className={`chip text-[10px] ${r.enabled ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-foreground/5"}`}>{r.enabled ? "live" : "off"}</span></td>
                <td className="px-3 py-2 text-right">
                  {r.enabled && <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`ann-del-${r.id}`}><Trash2 className="w-3 h-3"/></Button>}
                </td>
              </tr>
            ))}
            {(data.rows || []).length === 0 && <tr><td colSpan={6}><MgmtEmpty message="No announcements yet. Create one to broadcast a banner to all/role-scoped users."/></td></tr>}
          </tbody>
        </table>
      </div>
      <AnnouncementDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load}/>
    </div>
  );
}

function AnnouncementDialog({ open, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [severity, setSeverity] = useState("info");
  const [endsAt, setEndsAt] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTitle(""); setBody(""); setAudience("all"); setSeverity("info"); setEndsAt(""); setReason(""); } }, [open]);

  const submit = async () => {
    if (title.length < 2 || body.length < 2 || reason.trim().length < 3) { toast.error("All fields required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/mgmt/comms/announcements", {
        title, body, audience, severity, ends_at: endsAt || null, reason: reason.trim(),
      });
      toast.success("Announcement broadcasting"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ann-create-dialog">
        <DialogHeader><DialogTitle>New announcement</DialogTitle><DialogDescription>Broadcasts an in-app banner to the selected audience.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ann-title"/></div>
          <div><Label>Body</Label><Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} data-testid="ann-body"/></div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Audience</Label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="ann-audience">
                <option value="all">All</option><option value="buyer">Buyers</option><option value="seller">Sellers</option><option value="admin">Admins</option>
              </select>
            </div>
            <div>
              <Label>Severity</Label>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-background" data-testid="ann-severity">
                <option value="info">Info</option><option value="warn">Warn</option><option value="critical">Critical</option>
              </select>
            </div>
            <div><Label>Ends (optional)</Label><Input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="ann-ends"/></div>
          </div>
          <ReasonField value={reason} onChange={setReason}/>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="ann-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Broadcast
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
